/**
 * Shared Import Action Constants
 *
 * This file contains shared import action definitions used across
 * ImportSeedsModal and ImportAssetsModal to eliminate duplication.
 *
 * @module components/import/constants
 */

import type {
  AcceptedFileTypes,
  ImportAction,
} from '@/components/import/types';
import {
  validateCSV,
  validateJSON,
  validateSeedFile,
  validateXLSX,
  validateXML,
  validateZIP,
} from '@/components/import/utils';

/**
 * Common file type configurations
 */
const ACCEPTED_FILE_TYPES = {
  CSV: {
    accept: { 'text/csv': ['.csv'] } as AcceptedFileTypes,
    label: 'CSV',
  },
  JSON: {
    accept: { 'application/json': ['.json'] } as AcceptedFileTypes,
    label: 'JSON',
  },
  XML: {
    accept: {
      'text/xml': ['.xml'],
      'application/xml': ['.xml'],
    } as AcceptedFileTypes,
    label: 'XML',
  },
  ZIP: {
    accept: {
      'application/zip': ['.zip'],
      'application/x-zip-compressed': ['.zip'],
    } as AcceptedFileTypes,
    label: 'ZIP',
  },
  XLSX: {
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx',
      ],
      'application/vnd.ms-excel': ['.xls'],
    } as AcceptedFileTypes,
    label: 'XLSX',
  },
  SEED_FILES: {
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx',
      ],
      'text/csv': ['.csv'],
      'text/plain': ['.txt'],
    } as AcceptedFileTypes,
    label: 'XLSX, CSV, or TXT',
  },
};

/**
 * Sharphound import action
 * Used for importing Active Directory assets from Sharphound ZIP exports
 */
export const SHARPHOUND_IMPORT_ACTION: ImportAction = {
  label: 'Sharphound',
  value: 'sharphound',
  description: 'Import Active Directory assets from Sharphound ZIP export',
  acceptedFileTypes: ACCEPTED_FILE_TYPES.ZIP.accept,
  acceptedFormatsLabel: ACCEPTED_FILE_TYPES.ZIP.label,
  validation: {
    enabled: true,
    previewRows: 10,
    validate: validateZIP,
  },
};

/**
 * NSA CSaaS ASM import action
 * Used for importing assets discovered from NSA Cybersecurity as a Service Attack Surface Management
 */
export const NSA_CSAAS_IMPORT_ACTION: ImportAction = {
  label: 'NSA CSaaS ASM (Assets)',
  value: 'nsa-csaas-assets',
  description:
    'Import assets discovered from NSA Cybersecurity as a Service Attack Surface Management',
  acceptedFileTypes: ACCEPTED_FILE_TYPES.XLSX.accept,
  acceptedFormatsLabel: ACCEPTED_FILE_TYPES.XLSX.label,
  validation: {
    enabled: true,
    previewRows: 10,
    validate: validateXLSX,
  },
};

/**
 * Seed import action
 * Used for importing seeds from XLSX, CSV, or TXT files
 * Includes auto_approve configuration option
 */
export const SEED_IMPORT_ACTION: ImportAction = {
  label: 'Seed',
  value: 'seed',
  description:
    'Import domains, IPs, CIDRs, and web applications XLSX, CSV, or text',
  acceptedFileTypes: ACCEPTED_FILE_TYPES.SEED_FILES.accept,
  acceptedFormatsLabel: ACCEPTED_FILE_TYPES.SEED_FILES.label,
  validation: {
    enabled: true,
    previewRows: 10,
    validate: validateSeedFile,
  },
  configs: [
    {
      key: 'auto_approve',
      type: 'checkbox',
      label: 'Auto-approve imported seeds',
      description: 'Set seeds to Active status immediately instead of Pending',
      defaultValue: false,
    },
  ],
};

/**
 * All import actions for seed imports
 * Used by ImportSeedsModal
 */
export const SEED_IMPORT_ACTIONS: ImportAction[] = [
  SHARPHOUND_IMPORT_ACTION,
  NSA_CSAAS_IMPORT_ACTION,
  SEED_IMPORT_ACTION,
];

/**
 * All import actions for asset imports
 * Used by ImportAssetsModal
 * Note: Uses same actions as seeds with "Seed Import" label variant
 */
export const ASSET_IMPORT_ACTIONS: ImportAction[] = [
  SHARPHOUND_IMPORT_ACTION,
  NSA_CSAAS_IMPORT_ACTION,
  {
    ...SEED_IMPORT_ACTION,
    label: 'Seed Import', // Asset modal uses "Seed Import" instead of "Seed"
  },
];

/**
 * Qualys vulnerability import action
 * Used for importing vulnerabilities from Qualys scan results
 */
export const QUALYS_IMPORT_ACTION: ImportAction = {
  label: 'Qualys',
  value: 'qualys',
  description: 'Import vulnerabilities from Qualys scan results',
  acceptedFileTypes: ACCEPTED_FILE_TYPES.CSV.accept,
  acceptedFormatsLabel: ACCEPTED_FILE_TYPES.CSV.label,
  validation: {
    enabled: true,
    previewRows: 10,
    validate: validateCSV,
  },
};

/**
 * Nessus vulnerability import action
 * Used for importing vulnerabilities from Nessus scan reports
 */
export const NESSUS_IMPORT_ACTION: ImportAction = {
  label: 'Nessus',
  value: 'nessus',
  description: 'Import vulnerabilities from Nessus scan reports',
  acceptedFileTypes: ACCEPTED_FILE_TYPES.XML.accept,
  acceptedFormatsLabel: ACCEPTED_FILE_TYPES.XML.label,
  validation: {
    enabled: true,
    previewRows: 10,
    validate: validateXML,
  },
};

/**
 * InsightVM vulnerability import action
 * Used for importing vulnerabilities from Rapid7 InsightVM
 */
export const INSIGHTVM_IMPORT_ACTION: ImportAction = {
  label: 'InsightVM',
  value: 'insightvm',
  description: 'Import vulnerabilities from Rapid7 InsightVM',
  acceptedFileTypes: ACCEPTED_FILE_TYPES.JSON.accept,
  acceptedFormatsLabel: ACCEPTED_FILE_TYPES.JSON.label,
  validation: {
    enabled: true,
    previewRows: 10,
    validate: validateJSON,
  },
};

/**
 * CISA Cyber Hygiene Vulnerability Scan import action
 * Used for importing from CISA Cyber Hygiene vulnerability scanning service
 */
export const CISA_CH_VULNSCAN_IMPORT_ACTION: ImportAction = {
  label: 'CISA Cyber Hygiene Vulnerability Scan',
  value: 'cisa-ch-vulnscan',
  description: 'Import from CISA Cyber Hygiene vulnerability scanning service',
  acceptedFileTypes: ACCEPTED_FILE_TYPES.CSV.accept,
  acceptedFormatsLabel: ACCEPTED_FILE_TYPES.CSV.label,
  validation: {
    enabled: true,
    previewRows: 10,
    validate: validateCSV,
  },
};

/**
 * CISA Cyber Hygiene Web Application Scan import action
 * Used for importing from CISA Cyber Hygiene web application scanning service
 */
export const CISA_CH_WEBAPPSCAN_IMPORT_ACTION: ImportAction = {
  label: 'CISA Cyber Hygiene Web Application Scan',
  value: 'cisa-ch-webappscan',
  description:
    'Import from CISA Cyber Hygiene web application scanning service',
  acceptedFileTypes: ACCEPTED_FILE_TYPES.CSV.accept,
  acceptedFormatsLabel: ACCEPTED_FILE_TYPES.CSV.label,
  validation: {
    enabled: true,
    previewRows: 10,
    validate: validateCSV,
  },
};

/**
 * NSA CSaaS ASM (Vulnerabilities) import action
 * Used for importing vulnerabilities and associated assets discovered from NSA Cybersecurity as a Service Attack Surface Management
 */
export const NSA_CSAAS_VULNS_IMPORT_ACTION: ImportAction = {
  label: 'NSA CSaaS ASM (Vulnerabilities)',
  value: 'nsa-csaas-vulns',
  description:
    'Import vulnerabilities and associated assets discovered from NSA Cybersecurity as a Service Attack Surface Management',
  acceptedFileTypes: ACCEPTED_FILE_TYPES.XLSX.accept,
  acceptedFormatsLabel: ACCEPTED_FILE_TYPES.XLSX.label,
  validation: {
    enabled: true,
    previewRows: 10,
    validate: validateXLSX,
  },
};

/**
 * All import actions for vulnerability imports
 * Used by ImportVulnerabilitiesModal
 */
export const VULNERABILITY_IMPORT_ACTIONS: ImportAction[] = [
  QUALYS_IMPORT_ACTION,
  NESSUS_IMPORT_ACTION,
  INSIGHTVM_IMPORT_ACTION,
  CISA_CH_VULNSCAN_IMPORT_ACTION,
  CISA_CH_WEBAPPSCAN_IMPORT_ACTION,
  NSA_CSAAS_VULNS_IMPORT_ACTION,
];
